import React, { useEffect, useState } from 'react';

import type { Dayjs } from 'dayjs';
import dayjs from '~/timeInit';

import { Box, Button, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import { dateRangeError } from './dateRange';
import type { PerformanceDateRange } from './types';

export default function PerformanceDateRangePicker({
  range,
  reportDate,
  onApply,
}: {
  range: PerformanceDateRange;
  reportDate: string;
  onApply: (range: PerformanceDateRange) => void;
}) {
  const [start, setStart] = useState<Dayjs | null>(dayjs(range.start_date));
  const [end, setEnd] = useState<Dayjs | null>(dayjs(range.end_date));
  useEffect(() => {
    setStart(dayjs(range.start_date));
    setEnd(dayjs(range.end_date));
  }, [range.start_date, range.end_date]);
  const draft = { start_date: start?.format('YYYY-MM-DD') ?? '', end_date: end?.format('YYYY-MM-DD') ?? '' };
  const error = dateRangeError(draft, reportDate);
  const changed = draft.start_date !== range.start_date || draft.end_date !== range.end_date;
  return (
    <Box>
      <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
        <DatePicker
          label="Start date"
          format="DD MMM YYYY"
          value={start}
          onChange={setStart}
          minDate={dayjs(reportDate).subtract(89, 'day')}
          maxDate={dayjs(reportDate)}
          slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
        />
        <DatePicker
          label="End date"
          format="DD MMM YYYY"
          value={end}
          onChange={setEnd}
          minDate={start?.isValid() ? start : dayjs(reportDate).subtract(89, 'day')}
          maxDate={dayjs(reportDate)}
          slotProps={{ textField: { size: 'small', sx: { width: 170 } } }}
        />
        <Button variant="outlined" size="small" disabled={!changed || !!error} onClick={() => onApply(draft)}>
          Apply
        </Button>
      </Stack>
      {changed && !error && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
          Apply to update this page.
        </Typography>
      )}
      {changed && error && (
        <Typography variant="caption" color="error" role="alert" sx={{ mt: 0.5, display: 'block' }}>
          {error}
        </Typography>
      )}
    </Box>
  );
}
